import React, { useEffect, useState, useCallback } from 'react';
import { ArrowDownIcon, DeleteIcon, EditIcon, SearchIcon } from '../assets/icons';
import { getAllAssignments, getAllAssignmentsExcel } from '../services/vehicleService';
import { formatPlate } from '../helpers/stringHelpers';
import ActionButton from '../components/common/ActionButton';
import CreateAssignmentModal from '../components/AssignmentModals/CreateAssignmentModal';
import DeleteAssignmentModal from '../components/AssignmentModals/DeleteAssignmentModal';
import EditAssignmentModal from '../components/AssignmentModals/EditAssignmentModal';
import CompleteAssignmentModal from '../components/AssignmentModals/CompleteAssignmentModal';
import AssignmentDetailModal from '../components/AssignmentModals/AssignmentDetailModal';
import Pagination from '../components/Pagination';

const TIME_PERIODS = [
  { label: 'Tümü', value: '' },
  { label: 'Bugün', value: 0 },
  { label: '1 Hafta', value: 7 },
  { label: '1 Ay', value: 30 },
  { label: '1 Yıl', value: 365 }
];

const Vehicle = () => {
  const [assignmentData, setAssignmentData] = useState({
    workingVehicleCount: 0,
    workingPersonnelCount: 0,
    workingDistrictCount: 0,
    assignments: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDays, setSelectedDays] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  useEffect(() => {
    fetchAssignmentData();
  }, [currentPage, selectedDays]);

  const fetchAssignmentData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = {};

      if (selectedDays) {
        params.days = selectedDays;
      } else {
        params.date = new Date().toISOString().split('T')[0];
      }

      const data = await getAllAssignments(params);

      if (data.status === 200) {
        setAssignmentData(data);
        setTotalPages(Math.ceil(data.assignments.length / 15));
      } else {
        setAssignmentData({
          workingVehicleCount: 0,
          workingPersonnelCount: 0,
          workingDistrictCount: 0,
          assignments: []
        });
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Failed to fetch assignment data:', error);
      setError('Vasıta Verileri yüklenirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handlePeriodChange = (e) => {
    setSelectedDays(e.target.value ? Number(e.target.value) : '');
    setCurrentPage(0);
  };

  const handleModalClose = useCallback(() => {
    setCreateModalOpen(false);
  }, []);

  const handleCreateSuccess = useCallback((newAssignment) => {
    // Update the assignment list with the new assignment
    setAssignmentData(prevData => {
      // Create a copy of the previous assignments
      const updatedAssignments = [
        // Add the new assignment at the beginning
        newAssignment,
        // Add all existing assignments
        ...prevData.assignments
      ];

      // Return updated data with new assignment included
      return {
        ...prevData,
        workingVehicleCount: prevData.workingVehicleCount + 1,
        workingPersonnelCount: prevData.workingPersonnelCount + newAssignment.users.length,
        assignments: updatedAssignments
      };
    });
  }, []);

  // Handle successful edit of an assignment
  const handleEditSuccess = useCallback((updatedAssignment) => {
    // Update the assignment list by replacing the edited assignment
    setAssignmentData(prevData => {
      // Find the index of the assignment being edited
      const assignmentIndex = prevData.assignments.findIndex(
        assignment => assignment.assigmentId === updatedAssignment.assigmentId
      );

      if (assignmentIndex === -1) {
        // If assignment not found, just return the current data
        return prevData;
      }

      // Calculate the change in personnel count
      const oldUserCount = prevData.assignments[assignmentIndex].users.length;
      const newUserCount = updatedAssignment.users.length;
      const personnelCountDiff = newUserCount - oldUserCount;

      // Create a copy of the assignments array with the updated assignment
      const updatedAssignments = [...prevData.assignments];
      updatedAssignments[assignmentIndex] = updatedAssignment;

      // Return updated data with the edited assignment
      return {
        ...prevData,
        workingPersonnelCount: prevData.workingPersonnelCount + personnelCountDiff,
        assignments: updatedAssignments
      };
    });
  }, []);

  // Handle successful completion of an assignment (adding scale information)
  const handleCompleteSuccess = useCallback((completedAssignmentId, scaleInfo) => {
    // Update the assignment list by adding scale information to the completed assignment
    setAssignmentData(prevData => {
      // Find the index of the assignment being completed
      const assignmentIndex = prevData.assignments.findIndex(
        assignment => assignment.assigmentId === completedAssignmentId
      );

      if (assignmentIndex === -1) {
        // If assignment not found, just return the current data
        return prevData;
      }

      // Create a copy of the assignments array
      const updatedAssignments = [...prevData.assignments];

      // Update the assignment with scale information
      updatedAssignments[assignmentIndex] = {
        ...updatedAssignments[assignmentIndex],
        scaleInformation: scaleInfo,
        scaleDatetime: new Date().toISOString() // Add current timestamp
      };

      // Return updated data with the completed assignment
      return {
        ...prevData,
        assignments: updatedAssignments
      };
    });
  }, []);

  // Handle successful deletion of an assignment
  const handleDeleteSuccess = useCallback((deletedAssignmentId) => {
    // Update the assignment list by removing the deleted assignment
    setAssignmentData(prevData => {
      // Find the assignment to be deleted
      const assignmentIndex = prevData.assignments.findIndex(
        assignment => assignment.assigmentId === deletedAssignmentId
      );

      if (assignmentIndex === -1) {
        // If assignment not found, just return the current data
        return prevData;
      }

      // Get the deleted assignment to calculate personnel count change
      const deletedAssignment = prevData.assignments[assignmentIndex];
      const userCount = deletedAssignment.users.length;

      // Create a copy of the assignments array without the deleted assignment
      const updatedAssignments = prevData.assignments.filter(
        assignment => assignment.assigmentId !== deletedAssignmentId
      );

      // Return updated data without the deleted assignment
      return {
        ...prevData,
        workingVehicleCount: prevData.workingVehicleCount - 1,
        workingPersonnelCount: prevData.workingPersonnelCount - userCount,
        assignments: updatedAssignments
      };
    });
  }, []);

  const handleDeleteClick = (assignmentId, e) => {
    e.stopPropagation(); // Prevent row click event
    setSelectedAssignment(assignmentId);
    setDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setSelectedAssignment(null);
  };

  const handleEditClick = (assignment, e) => {
    e.stopPropagation(); // Prevent row click event
    setSelectedAssignment(assignment.assigmentId);
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setSelectedAssignment(null);
  };

  const handleCompleteClick = (assignment, e) => {
    e.stopPropagation(); // Prevent row click event
    setSelectedAssignment(assignment.assigmentId);
    setCompleteModalOpen(true);
  };

  const handleCompleteModalClose = () => {
    setCompleteModalOpen(false);
    setSelectedAssignment(null);
  };

  const handleRowClick = (assignment) => {
    setSelectedAssignment(assignment.assigmentId);
    setDetailModalOpen(true);
  };

  const handleDetailModalClose = () => {
    setDetailModalOpen(false);
    setSelectedAssignment(null);
  };

  const handleExcelDownload = async () => {
    try {
      // If selectedDays is provided, pass it; otherwise pass today's date
      const today = new Date().toISOString().split('T')[0];
      await getAllAssignmentsExcel(selectedDays, today);
    } catch (error) {
      console.error('Failed to download excel:', error);
      setError('Excel dosyası indirilemedi.');
    }
  };

  // Function to check if scale information is less than an hour old
  const isScaleInfoRecent = (scaleDatetime) => {
    if (!scaleDatetime) return false;

    const scaleDate = new Date(scaleDatetime);
    const currentDate = new Date();
    const diffInMilliseconds = currentDate - scaleDate;
    const diffInHours = diffInMilliseconds / (1000 * 60 * 60);

    return diffInHours < 1;
  };

  // Function to render the appropriate action buttons based on assignment state
  const renderActionButtons = (assignment) => {
    // Case 1: No scale information - show all buttons
    if (!assignment.scaleInformation) {
      return (
        <div className='user-actions-container'>
          <button
            className="icon-button edit"
            onClick={(e) => handleEditClick(assignment, e)}
            title="Düzenle"
          >
            Düzenle
            <EditIcon color='black' />
          </button>
          <button
            className="icon-button delete"
            onClick={(e) => handleDeleteClick(assignment.assigmentId, e)}
            title="Sil"
          >
            Sil
            <DeleteIcon color='#C70000' />
          </button>
          <button
            className='complete-button'
            onClick={(e) => handleCompleteClick(assignment, e)}
          >
            Tamamla
          </button>
        </div>
      );
    }

    // Case 2: Has scale info and it's less than an hour old
    if (isScaleInfoRecent(assignment.scaleDatetime)) {
      return (
        <div className='user-actions-container'>
          <button
            className="icon-button edit-scale-button"
            onClick={(e) => handleCompleteClick(assignment, e)}
          >
            Kantar Bilgisini Düzenle
            <EditIcon color='black' />
          </button>
        </div>
      );
    }

    // Case 3: Has scale info and it's more than an hour old
    return (
      <div className='user-actions-container completed-process'>
        <p>Süreç tamamlanmıştır.</p>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="waste-container">
        <h1 className="page-title">Vasıta Verileri</h1>
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="waste-container">
        <h1 className="page-title">Vasıta Verileri</h1>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="waste-container">
      <h1 className="page-title">Vasıta Verileri</h1>

      {/* Search and Filter Section */}
      <div className="search-filter-section">
        <ActionButton
          title="Yeni Görevlendirme Ekle"
          onClick={() => setCreateModalOpen(true)}
        />

        <div className="filter-container">
          <button className="download-excel-button" onClick={handleExcelDownload}>
            <p>Excel Çıktısı Al</p>
            <ArrowDownIcon color='white' className="filter-icon" />
          </button>
          <select value={selectedDays} onChange={handlePeriodChange}>
            {TIME_PERIODS.map(period => (
              <option key={period.value} value={period.value}>
                {period.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="statistics-section">
        <div className="stat-card-container separated-waste-statistics">
          <div className="stat-card">
            <h3>Çalışan<br />Araç Sayısı</h3>
            <p className='stat-card-number'>{assignmentData.workingVehicleCount}</p>
            <p className='stat-card-text'>araç</p>
          </div>
          <div className="stat-card green">
            <h3>Çalışmakta Olan<br />Personel Sayısı</h3>
            <p className='stat-card-number'>{assignmentData.workingPersonnelCount}</p>
            <p className='stat-card-text'>personel</p>
          </div>
          <div className="stat-card">
            <h3>Aktif Olunan<br />İlçe Sayısı</h3>
            <p className='stat-card-number'>{assignmentData.workingDistrictCount}</p>
            <p className='stat-card-text'>ilçe</p>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="table-container">
        <table className="waste-table">
          <thead>
            <tr>
              <th>Tarih</th>
              <th>Plaka No</th>
              <th>Araca Atanan<br />Kişiler</th>
              <th>Kantar Ağırlığı<br />(kg)</th>
              <th>Hedefteki Görevlendirme Yeri</th>
              <th>Kullanıcı Bazlı İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {assignmentData.assignments.length > 0 ? (
              assignmentData.assignments.map((assignment) => {
                const isCompleted = assignment.scaleInformation && !isScaleInfoRecent(assignment.scaleDatetime);

                return (
                  <tr
                    key={assignment.assigmentId}
                    onClick={() => handleRowClick(assignment)}
                    className={`clickable-row ${isCompleted ? 'completed-row' : ''}`}
                  >
                    <td><p className='badge date-badge'>{new Date(assignment.assignmentDate).toLocaleDateString('tr-TR')}</p></td>
                    <td className='plate-td'><p>{formatPlate(assignment.numberPlate)}</p></td>
                    <td className='plate-td'><p>{`${assignment.users.length} kişi atandı.`}</p></td>
                    <td>
                      {assignment.scaleInformation ? <p className='badge scale-badge'>{assignment.scaleInformation} Kg</p> : <p>-</p>}
                    </td>
                    <td className='district-td'><p>{assignment.district} <span>({assignment.neighbourHood})</span></p></td>
                    <td className='user-actions' onClick={(e) => e.stopPropagation()}>
                      {renderActionButtons(assignment)}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className="no-data">
                  Veri bulunamadı
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      <CreateAssignmentModal
        isOpen={createModalOpen}
        onClose={handleModalClose}
        onSuccess={handleCreateSuccess}
      />
      <DeleteAssignmentModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteModalClose}
        onSuccess={handleDeleteSuccess}
        assignmentId={selectedAssignment}
      />
      <EditAssignmentModal
        isOpen={editModalOpen}
        onClose={handleEditModalClose}
        onSuccess={handleEditSuccess}
        assignmentId={selectedAssignment}
      />
      <CompleteAssignmentModal
        isOpen={completeModalOpen}
        onClose={handleCompleteModalClose}
        onSuccess={handleCompleteSuccess}
        assignmentId={selectedAssignment}
      />
      <AssignmentDetailModal
        isOpen={detailModalOpen}
        onClose={handleDetailModalClose}
        assignmentId={selectedAssignment}
      />
    </div>
  );
};

export default Vehicle;